// src/context/PointsContext.jsx
// Fetches and caches the user's points balance + badge tier
// Exposed via usePoints() hook
// Re-fetches after saves via refreshPoints()

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';

const BADGE_TIERS = [
  { min: 5000, tier: 'Master',      emoji: '🔴' },
  { min: 1500, tier: 'Expert',      emoji: '🟡' },
  { min: 500,  tier: 'Builder',     emoji: '🟣' },
  { min: 100,  tier: 'Contributor', emoji: '🔵' },
  { min: 0,    tier: 'Newcomer',    emoji: '—'  },
];

export function getBadge(lifetime = 0) {
  return BADGE_TIERS.find(t => lifetime >= t.min) || BADGE_TIERS[BADGE_TIERS.length - 1];
}

const PointsContext = createContext({
  balance:  0,
  lifetime: 0,
  badge:    null,
  loading:  false,
  refreshPoints: () => {},
});

export function PointsProvider({ children }) {
  const { user } = useAuth();
  const [balance,  setBalance]  = useState(0);
  const [lifetime, setLifetime] = useState(0);
  const [loading,  setLoading]  = useState(false);

  const fetchPoints = useCallback(async () => {
    if (!user) { setBalance(0); setLifetime(0); setLoading(false); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('points_balance, points_lifetime')
        .eq('id', user.id)
        .single();
      if (error) { console.error('PointsContext fetch error:', error); return; }
      if (data) {
        setBalance(data.points_balance   || 0);
        setLifetime(data.points_lifetime || 0);
      }
    } catch (e) {
      console.error('PointsContext:', e);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchPoints(); }, [fetchPoints]);

  const badge = getBadge(lifetime);

  return (
    <PointsContext.Provider value={{ balance, lifetime, badge, loading, refreshPoints: fetchPoints }}>
      {children}
    </PointsContext.Provider>
  );
}

export function usePoints() {
  return useContext(PointsContext);
}
